import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Inbox, Info, KeyRound, Loader2, Mail, RefreshCw, Save, Send, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APP_PASSWORD_HELP = {
  office365: "Outlook / Microsoft 365: Microsoft account → Security → Advanced security options → App passwords. If sign-in fails with “SmtpClientAuthentication is disabled”, your Microsoft 365 admin must turn on Authenticated SMTP for this mailbox.",
  gmail: "Gmail: Google Account → Security → 2-Step Verification (on) → App passwords → create one for “Mail”. Use the 16-character code here.",
  yahoo: "Yahoo: Account Security → Generate app password.",
  icloud: "iCloud: appleid.apple.com → Sign-In and Security → App-Specific Passwords.",
  godaddy: "GoDaddy Workspace: use your normal mailbox password.",
  custom: "Ask your email host for the SMTP server, port (587 STARTTLS or 465 SSL) and login.",
};

export default function EfaxEmailProviderTab({ cfg, setCfg, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [testingLogin, setTestingLogin] = useState(false);
  const [loginResult, setLoginResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testNumber, setTestNumber] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [password, setPassword] = useState("");

  const presets = cfg.presets || {};
  const imapPresets = cfg.imap_presets || {};
  const preset = cfg.smtp_preset || "office365";
  const [testingImap, setTestingImap] = useState(false);
  const [imapResult, setImapResult] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const choosePreset = (value) => {
    const p = presets[value] || {};
    const ip = imapPresets[value] || {};
    setCfg((c) => ({
      ...c, smtp_preset: value, smtp_host: value === "custom" ? c.smtp_host : p.host, smtp_port: p.port || 587,
      imap_host: value === "custom" ? c.imap_host : ip.host, imap_port: ip.port || 993,
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/integrations/efax-email`, {
        enabled: !!cfg.enabled,
        fax_number: cfg.fax_number || "",
        receipt_email: cfg.receipt_email || "",
        smtp_preset: preset,
        smtp_host: cfg.smtp_host || "",
        smtp_port: Number(cfg.smtp_port) || 587,
        smtp_username: cfg.smtp_username || "",
        sender_email: cfg.sender_email || cfg.smtp_username || "",
        imap_enabled: !!cfg.imap_enabled,
        imap_host: cfg.imap_host || imapPresets[preset]?.host || "",
        imap_port: Number(cfg.imap_port) || 993,
        imap_folder: cfg.imap_folder || "INBOX",
        ...(password ? { smtp_password: password } : {}),
      });
      setPassword("");
      toast.success("eFax (email) settings saved");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const testLogin = async () => {
    setTestingLogin(true);
    setLoginResult(null);
    try {
      if (password) await save();
      const res = await axios.post(`${API}/settings/integrations/efax-email/test-login`);
      setLoginResult(res.data);
      res.data.status === "ok" ? toast.success("Mailbox connected") : toast.error("Mailbox login failed");
    } catch (e) {
      const err = e.response?.data?.detail || "Test failed";
      setLoginResult({ status: "error", error: err });
      toast.error(err);
    } finally {
      setTestingLogin(false);
    }
  };

  const testImap = async () => {
    setTestingImap(true);
    setImapResult(null);
    try {
      await save();
      const res = await axios.post(`${API}/settings/integrations/efax-email/test-imap`);
      setImapResult(res.data);
      res.data.status === "ok" ? toast.success("Inbox connected") : toast.error("Inbox connection failed");
    } catch (e) {
      const err = e.response?.data?.detail || "Test failed";
      setImapResult({ status: "error", error: err });
      toast.error(err);
    } finally {
      setTestingImap(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/fax/sync-inbox`);
      if (res.data.status === "ok") toast.success(`${res.data.imported} new fax${res.data.imported === 1 ? "" : "es"} imported (${res.data.scanned} emails checked)`);
      else toast.error(res.data.error || res.data.reason || "Sync failed");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/settings/integrations/efax-email/test`, { fax_number: testNumber });
      setTestResult(res.data);
      res.data.success ? toast.success("Test fax handed to eFax") : toast.error(res.data.error || "Test failed");
    } catch (e) {
      const err = e.response?.data?.detail || "Test failed";
      setTestResult({ success: false, error: err });
      toast.error(err);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="efax-email-tab">
      <div className="rounded-lg border bg-slate-50 p-3 text-sm space-y-2">
        <div className="flex items-center gap-2 font-medium"><Info className="w-4 h-4 text-slate-500" /> Use your regular eFax plan (no Corporate API needed)</div>
        <p className="text-slate-600">
          CarePilot connects to <b>your agency's own mailbox</b> and emails each fax as a PDF to <code className="px-1 bg-white border rounded">number@{cfg.send_domain || "efaxsend.com"}</code>.
          Because it's sent from your address — the one already registered on your eFax account — eFax accepts it. Staff keep faxing from the portal as usual.
        </p>
        <p className="text-xs text-slate-500">Incoming faxes still arrive as PDFs in that mailbox; eFax delivery receipts land there too.</p>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {cfg.enabled ? (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"><CheckCircle2 className="w-3 h-3" /> Enabled</span>
          ) : (
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border">Disabled</span>
          )}
          <span className="text-sm text-slate-600">eFax (email-to-fax)</span>
          {cfg.mailbox_connected && <span className="text-xs text-emerald-700">· mailbox connected</span>}
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="efax-email-enabled" className="text-sm">Enabled</Label>
          <Switch id="efax-email-enabled" checked={!!cfg.enabled} onCheckedChange={(v) => setCfg((p) => ({ ...p, enabled: v }))} data-testid="efax-email-enabled" />
        </div>
      </div>

      <div className="rounded-lg border p-3 space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium"><KeyRound className="w-4 h-4 text-slate-500" /> 1. Connect the mailbox registered with eFax</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Email provider</Label>
            <Select value={preset} onValueChange={choosePreset}>
              <SelectTrigger data-testid="efax-email-preset"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(presets).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="efax-email-user">Email address (mailbox login)</Label>
            <Input id="efax-email-user" type="email" placeholder="hhcinfo@youragency.com" value={cfg.smtp_username || ""} onChange={(e) => setCfg((p) => ({ ...p, smtp_username: e.target.value, sender_email: p.sender_email || "" }))} data-testid="efax-email-username" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="efax-email-pass">App password</Label>
            <Input id="efax-email-pass" type="password" placeholder={cfg.smtp_password_set ? "•••••••• (saved — enter to replace)" : "App password from your email provider"} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="efax-email-password" />
          </div>
          {preset === "custom" ? (
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2 space-y-1">
                <Label htmlFor="efax-email-host">SMTP server</Label>
                <Input id="efax-email-host" placeholder="smtp.yourhost.com" value={cfg.smtp_host || ""} onChange={(e) => setCfg((p) => ({ ...p, smtp_host: e.target.value }))} data-testid="efax-email-host" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="efax-email-port">Port</Label>
                <Input id="efax-email-port" type="number" value={cfg.smtp_port || 587} onChange={(e) => setCfg((p) => ({ ...p, smtp_port: e.target.value }))} data-testid="efax-email-port" />
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              <Label>Mail server</Label>
              <p className="text-sm text-slate-600 pt-2">{cfg.smtp_host || presets[preset]?.host} : {cfg.smtp_port || presets[preset]?.port}</p>
            </div>
          )}
        </div>
        <p className="text-xs text-slate-500">{APP_PASSWORD_HELP[preset]}</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={testLogin} disabled={testingLogin || !(cfg.smtp_username && (password || cfg.smtp_password_set))} data-testid="efax-email-test-login">
            {testingLogin ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1" />} Test mailbox login
          </Button>
          {loginResult && (
            loginResult.status === "ok"
              ? <span className="text-xs text-emerald-700 inline-flex items-center gap-1" data-testid="efax-email-login-ok"><CheckCircle2 className="w-3.5 h-3.5" /> Connected</span>
              : <span className="text-xs text-rose-700 inline-flex items-start gap-1" data-testid="efax-email-login-error"><XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {loginResult.error}</span>
          )}
        </div>
      </div>

      <div className="rounded-lg border p-3 space-y-3">
        <div className="text-sm font-medium">2. Your eFax details</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="efax-email-number">Your eFax number</Label>
            <Input id="efax-email-number" placeholder="(555) 123-4567" value={cfg.fax_number || ""} onChange={(e) => setCfg((p) => ({ ...p, fax_number: e.target.value }))} data-testid="efax-email-number" />
            <p className="text-xs text-slate-500">Printed on the cover sheet as the sender fax.</p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="efax-email-sender">Send faxes from (if different from login)</Label>
            <Input id="efax-email-sender" type="email" placeholder={cfg.smtp_username || "same as mailbox login"} value={cfg.sender_email || ""} onChange={(e) => setCfg((p) => ({ ...p, sender_email: e.target.value }))} data-testid="efax-email-sender" />
            <p className="text-xs text-slate-500">Must be an address authorized on your eFax account (eFax → My Account → Send Preferences).</p>
          </div>
        </div>
        <p className="text-xs text-slate-500">Tip: turn off eFax's own cover page in Send Preferences — CarePilot already adds a branded cover sheet.</p>
      </div>

      <div className="rounded-lg border p-3 space-y-3" data-testid="efax-email-imap-section">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-medium flex items-center gap-1"><Inbox className="w-4 h-4" /> 3. Incoming faxes — inbox sync</div>
            <p className="text-xs text-slate-500 mt-0.5">
              Every 15 minutes CarePilot reads this same mailbox and files emails from eFax (and other fax services) with a PDF or TIFF into your Faxes inbox. Other mail is ignored.
            </p>
          </div>
          <Switch checked={!!cfg.imap_enabled} onCheckedChange={(v) => setCfg((p) => ({ ...p, imap_enabled: v }))} data-testid="efax-email-imap-enabled" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label htmlFor="efax-imap-host">IMAP server</Label>
            <Input id="efax-imap-host" placeholder={imapPresets[preset]?.host || "imap.yourhost.com"} value={cfg.imap_host || ""} onChange={(e) => setCfg((p) => ({ ...p, imap_host: e.target.value }))} data-testid="efax-email-imap-host" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="efax-imap-port">Port</Label>
            <Input id="efax-imap-port" type="number" value={cfg.imap_port || 993} onChange={(e) => setCfg((p) => ({ ...p, imap_port: e.target.value }))} data-testid="efax-email-imap-port" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="efax-imap-folder">Folder</Label>
            <Input id="efax-imap-folder" placeholder="INBOX" value={cfg.imap_folder || ""} onChange={(e) => setCfg((p) => ({ ...p, imap_folder: e.target.value }))} data-testid="efax-email-imap-folder" />
          </div>
        </div>
        <p className="text-xs text-slate-500">Uses the mailbox login and app password from step 1. Gmail: enable IMAP under Gmail Settings → Forwarding and POP/IMAP. Microsoft 365: an admin may need to turn IMAP on for the mailbox.</p>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={testImap} disabled={testingImap || !(cfg.smtp_username && (password || cfg.smtp_password_set))} data-testid="efax-email-test-imap">
            {testingImap ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1" />} Test inbox connection
          </Button>
          <Button variant="outline" size="sm" onClick={syncNow} disabled={syncing || !cfg.imap_enabled} data-testid="efax-email-sync-now">
            {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Sync now
          </Button>
          {imapResult && (
            imapResult.status === "ok"
              ? <span className="text-xs text-emerald-700 inline-flex items-center gap-1" data-testid="efax-email-imap-ok"><CheckCircle2 className="w-3.5 h-3.5" /> Connected · {imapResult.messages} emails in {imapResult.folder}</span>
              : <span className="text-xs text-rose-700 inline-flex items-start gap-1" data-testid="efax-email-imap-error"><XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {imapResult.error}</span>
          )}
        </div>
        {cfg.imap_last_sync && (
          <p className={`text-xs ${cfg.imap_last_sync.ok ? "text-slate-500" : "text-rose-700"}`} data-testid="efax-email-imap-last-sync">
            Last sync {new Date(cfg.imap_last_sync.at).toLocaleString()}: {cfg.imap_last_sync.ok ? `${cfg.imap_last_sync.imported} imported, ${cfg.imap_last_sync.scanned ?? 0} emails checked` : cfg.imap_last_sync.error}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={save} disabled={saving} data-testid="efax-email-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
        </Button>
        <div className="flex items-center gap-2 ml-auto">
          <Input placeholder="Test fax number" value={testNumber} onChange={(e) => setTestNumber(e.target.value)} className="h-9 w-44" data-testid="efax-email-test-number" />
          <Button variant="outline" onClick={sendTest} disabled={testing || !testNumber.trim() || !cfg.enabled || !cfg.mailbox_connected} data-testid="efax-email-test-btn">
            {testing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send test fax
          </Button>
        </div>
      </div>

      {testResult && (
        <div className={`rounded-lg border p-3 text-sm ${testResult.success ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`} data-testid="efax-email-test-result">
          {testResult.success ? (
            <>
              <div className="font-medium flex items-center gap-1"><Mail className="w-4 h-4" /> Sent to {testResult.sent_to}</div>
              <div className="text-xs mt-0.5">{testResult.note}</div>
            </>
          ) : (
            <>
              <div className="font-medium">✗ Test fax failed</div>
              <div className="text-xs mt-0.5 whitespace-pre-wrap break-words">{testResult.error}</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
